import api from './api';
import type { VenueLocation } from '../types/venue';

const base = (venueId: string) => `/admin/venue/${venueId}/location`;

/**
 * Pick the default active location from a list.
 * Prefers the location with isDefault === true; falls back to the first entry.
 * Pass in an already-filtered (active-only) list.
 */
export const pickDefaultLocation = (locations: VenueLocation[]): VenueLocation | undefined =>
  locations.find(l => l.isDefault) ?? locations[0];

/**
 * WAREHOUSE-LOCATIONS.2 — how a location should read in a picker.
 *
 * Names are unique within a WAREHOUSE, not within a venue, so a venue reaching
 * two warehouses can legitimately see two "General Stock". A bare name makes
 * them indistinguishable at exactly the moment someone is choosing where stock
 * goes.
 *
 * WAREHOUSE-LOCATIONS.5 — the warehouse is now ALWAYS shown when there is one.
 *
 * This used to qualify the name only when the label would otherwise be
 * ambiguous, on the reasoning that "Main Store · " on every row of a
 * single-warehouse venue is noise. That was wrong twice over. A reader cannot
 * tell a quiet label from a missing one, so the rule made the UI look broken
 * rather than concise — and which warehouse stock lands in is material even
 * when only one is on offer, because the reader is deciding whether it is the
 * right one, not merely telling it apart from another.
 *
 * A rule people can predict beats one tuned to save a few characters.
 */
export const locationLabel = (location: VenueLocation): string => {
  // WAREHOUSE-LOCATIONS.6 — prefix with whichever name identifies the place.
  //
  // Every venue's home warehouse is auto-named "Main Store" and its first
  // location "General Stock", so within a company those collide by
  // construction: two rows reading "Main Store · General Stock" with nothing
  // separating them. The warehouse name carries no information there. The
  // owning VENUE always does.
  //
  // Your own warehouse and shared ones keep the warehouse name — "Main Store"
  // reads correctly inside your own venue, and a shared cellar belongs to the
  // company rather than to anyone whose name could stand in for it.
  const prefix = location.ownerVenueName ?? location.warehouseName;
  return prefix ? `${prefix} · ${location.name}` : location.name;
};

/**
 * Stamp `displayName` on every location in a fetched list.
 *
 * Done once here rather than at each render site, because there are two dozen
 * of them — dropdowns, headers, id→name maps — and a rule applied at only some
 * is worse than no rule: the same location reads differently depending on which
 * screen you are on.
 *
 * `name` is left untouched, because it is what edit forms submit and what the
 * server's uniqueness is keyed on. Display and identity are different jobs.
 */
const withDisplayNames = (locations: VenueLocation[]): VenueLocation[] =>
  locations.map(l => ({ ...l, displayName: locationLabel(l) }));

const venueLocationService = {
  /**
   * Every location this venue may act on — its own plus any it reaches by
   * grant, each carrying its warehouse.
   *
   * `permission` narrows to what the venue may actually DO there. A receiving
   * screen passes RECEIVE so it never offers a cellar the venue can only sell
   * from, which would be a choice that fails on save.
   *
   * `includeInactive` adds retired locations, for a report filter: their
   * history is still in the ledger.
   */
  getLocations: async (
    venueId: string,
    permission?: string,
    opts: { includeInactive?: boolean } = {},
  ): Promise<VenueLocation[]> => {
    const params: Record<string, string> = {};
    if (permission) params.permission = permission;
    if (opts.includeInactive) params.includeInactive = 'true';
    const response = await api.get(base(venueId), {
      params: Object.keys(params).length ? params : undefined,
    });
    return withDisplayNames(response.data);
  },

  /**
   * Get a single location.
   *
   * Its `displayName` always carries the warehouse when there is one. Ambiguity
   * cannot be judged from one row, and these are headers ("Transfer from …")
   * rather than repeated list entries, so naming the warehouse reads as useful
   * context rather than as noise.
   */
  getLocation: async (venueId: string, locationId: string): Promise<VenueLocation> => {
    const response = await api.get(`${base(venueId)}/${locationId}`);
    const loc = response.data as VenueLocation;
    return { ...loc, displayName: locationLabel(loc) };
  },

  /**
   * WAREHOUSE-ADDRESS.1 — createLocation, updateLocation and deleteLocation are
   * gone from here. They posted to the venue, and the venue route never took a
   * warehouse: it created in the venue's PRIMARY whichever one the operator
   * meant, which is why adding "General Stock" to Alfred's reported that it
   * already existed. The API answers those three with 410 now.
   *
   * They live in warehouseLocationService, addressed by the warehouse that
   * actually holds the location. Deactivation is an update there rather than a
   * DELETE, because it always was one underneath.
   *
   * The READS stay here. "Every location this venue may act on" spans several
   * warehouses and is answered from entitlement — there is no single warehouse
   * to address it to, and twenty screens ask it.
   */
};

export default venueLocationService;
